import React, { useState, useEffect, useCallback } from "react";
import QuestionItem from "../components/QuestionItem";
import QuestionsAnswersArray from "../components/QuestionsAnswersArray";

/**
 * Efficacy Analysis questions – page index starts at 1 on this page.
 * Single-arm: Q1–Q9.
 * Multiple-arm / dose-finding: Q1–Q8.
 *
 * Activators include the flow branch check so that only the correct variant
 * is visible at any time, even though both variants live in the same form.
 */
const EfficacyAnalysisQuestions = ({
  globalId,
  wizardForm,
  isRestoringRef,
}) => {
  // Local state used purely to force re-renders when form values change so
  // that activator props (which read wizardForm.controls.xxx.value) update.
  const [, forceUpdate] = useState(0);

  const isEfficacy = wizardForm.controls.qIsEfficacy?.value === "true";
  const isPrimary = wizardForm.controls.qPrimaryEfficacy?.value === "true";

  // ── handlers (mirrors the logic in the original Questions component) ──────

  const onChangeIsEfficacy = useCallback(() => {
    if (isRestoringRef?.current) return;
    if (wizardForm.controls.qIsEfficacy.dirty) {
      [
        "qEfficacyNonComp",
        "qEfficacyAnalytes",
        "qPrimaryEfficacyComparisonNames",
        "qEfficacyPopulations",
        "qEfficacyAnalysisTypeNames",
        "qEfficacyBaseline",
        "qEfficacyTimepointNames",
      ].forEach(controlName => {
        if (wizardForm.controls[controlName]) {
          wizardForm.controls[controlName].updateValueAndValidity();
          wizardForm.controls[controlName].reset();
        }
      });
    }
  }, [wizardForm]);

  const onChangePrimaryEfficacy = useCallback(() => {
    if (isRestoringRef?.current) return;
    const parentFields = [
      "qEfficacyNonComp",
      "qEfficacyAnalytes",
      "qPrimaryEfficacyComparisonNames",
      "qEfficacyPopulations",
      "qEfficacyAnalysisTypeNames",
      "qEfficacyBaseline",
      "qEfficacyTimepointNames",
    ];
    const hasParentChanged = parentFields.some(
      field => wizardForm.controls[field]?.dirty
    );
    if (hasParentChanged) {
      [
        "qPrimaryEfficacyAnalytes",
        "qPrimaryEfficacyPopulation",
        "qPrimaryEfficacyAnalysisTypeNames",
        "qPrimaryEfficacyAnalytesComparison",
        "qPrimaryEfficacyBaseline",
        "qPrimaryEfficacyTimepointNames",
        "qPrimaryEfficacySensitivityNames",
        "qEfficacyKaplanMeierAnalytes",
      ].forEach(controlName => {
        if (wizardForm.controls[controlName]) {
          wizardForm.controls[controlName].updateValueAndValidity();
          wizardForm.controls[controlName].reset();
        }
      });
    }
  }, [wizardForm]);

  // ── subscriptions ──────────────────────────────────────────────────────────

  useEffect(() => {
    wizardForm.controls.qIsEfficacy.valueChanges.subscribe(() => {
      onChangeIsEfficacy();
      forceUpdate(v => v + 1);
    });
    return () => {
      wizardForm.controls.qIsEfficacy.valueChanges.unsubscribe();
    };
  }, [wizardForm.controls.qIsEfficacy.value]);

  useEffect(() => {
    wizardForm.controls.qPrimaryEfficacy.valueChanges.subscribe(() => {
      onChangePrimaryEfficacy();
      forceUpdate(v => v + 1);
    });
    return () => {
      wizardForm.controls.qPrimaryEfficacy.valueChanges.unsubscribe();
    };
  }, [wizardForm.controls.qPrimaryEfficacy.value]);

  // Subscribe to controls that feed into onChangePrimaryEfficacy, matching
  // the original Questions component subscription list.
  useEffect(() => {
    const subscriptions = [];
    const controlNames = [
      "qEfficacyNonComp",
      "qEfficacyAnalytes",
      "qPrimaryEfficacyComparisonNames",
      "qEfficacyPopulations",
      "qEfficacyAnalysisTypeNames",
      "qEfficacyBaseline",
      "qEfficacyTimepointNames",
      "qTgNamesComparison",
    ];
    controlNames.forEach(controlName => {
      if (wizardForm.controls[controlName]) {
        const sub = wizardForm.controls[controlName].valueChanges.subscribe(
          () => {
            onChangePrimaryEfficacy();
            forceUpdate(v => v + 1);
          }
        );
        subscriptions.push(sub);
      }
    });
    return () => {
      subscriptions.forEach(sub => {
        if (sub && typeof sub.unsubscribe === "function") {
          sub.unsubscribe();
        }
      });
    };
  }, [wizardForm, onChangePrimaryEfficacy]);

  // Subscribe to qTgNames so that the Q10b Treatment/Comparator dropdowns
  // refresh their options as the user fills in treatment-group names.
  useEffect(() => {
    wizardForm.controls.qTgNames.valueChanges.subscribe(() => {
      forceUpdate(v => v + 1);
    });
    return () => {
      wizardForm.controls.qTgNames.valueChanges.unsubscribe();
    };
  }, []);

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <form name={"wizardForm"}>
      {/* ════════════════════════════════════════════════
          SINGLE ARM  (qComparative = false)
          ════════════════════════════════════════════════ */}

      {/* Q1 – is there an efficacy analysis? */}
      <QuestionItem
        questionIndex={"1"}
        questionName={"qIsEfficacy"}
        activator={wizardForm.controls.qComparative.value === "false"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />
      {/* Q2 – non-comparative efficacy */}
      {/* <QuestionItem
        questionIndex={"2"}
        questionName={"qEfficacyNonComp"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      /> */}
      {/* Q3 – analytes */}
      <QuestionItem
        questionIndex={"2"}
        questionName={"qEfficacyAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        formGroup={wizardForm}
        isMultiSelectDropdown={true}
        globalId={globalId}
      />
      {/* Q4 – populations */}
      <QuestionsAnswersArray
        questionIndex={"3"}
        controlName={"qEfficacyPopulations"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q5 – analysis type names */}
      <QuestionsAnswersArray
        questionIndex={"4"}
        controlName={"qEfficacyAnalysisTypeNames"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q6 – baseline – hidden per manager request; logic/handlers kept intact
      <QuestionItem
        questionIndex={"6"}
        questionName={"qEfficacyBaseline"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        questionInputType={"text"}
      />
      */}
      {/* Q6 – timepoint names */}
      <QuestionsAnswersArray
        questionIndex={"5"}
        controlName={"qEfficacyTimepointNames"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q7 – primary efficacy? */}
      <QuestionItem
        questionIndex={"6"}
        questionName={"qPrimaryEfficacy"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />
      {/* Q7a-Q7e – primary efficacy sub-questions */}
      <QuestionsAnswersArray
        questionIndex={"6a"}
        controlName={"qPrimaryEfficacyAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "false" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyAnalytes}
      />
      <QuestionsAnswersArray
        questionIndex={"6b"}
        controlName={"qPrimaryEfficacyPopulation"}
        activator={
          wizardForm.controls.qComparative.value === "false" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyPopulations}
      />
      <QuestionsAnswersArray
        questionIndex={"6c"}
        controlName={"qPrimaryEfficacyAnalysisTypeNames"}
        activator={
          wizardForm.controls.qComparative.value === "false" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={
          wizardForm.value.qEfficacyAnalysisTypeNames
        }
      />
      <QuestionsAnswersArray
        questionIndex={"6d"}
        controlName={"qPrimaryEfficacyAnalytesComparison"}
        activator={
          wizardForm.controls.qComparative.value === "false" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* <QuestionsAnswersArray
        questionIndex={"7e"}
        controlName={"qPrimaryEfficacyBaseline"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isPrimary
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyBaseline}
        maxInputs={1}
      /> */}
      <QuestionsAnswersArray
        questionIndex={"6e"}
        controlName={"qPrimaryEfficacyTimepointNames"}
        activator={
          wizardForm.controls.qComparative.value === "false" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyTimepointNames}
      />
      {/* Q8 – Kaplan-Meier plots (single-arm) */}
      <QuestionsAnswersArray
        questionIndex={"7"}
        controlName={"qEfficacyKaplanMeierAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "false" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />

      {/* ════════════════════════════════════════════════
          MULTIPLE ARM & DOSE FINDING  (qComparative = true)
          ════════════════════════════════════════════════ */}

      {/* Q1 – is there an efficacy analysis? */}
      <QuestionItem
        questionIndex={"1"}
        questionName={"qIsEfficacy"}
        activator={wizardForm.controls.qComparative.value === "true"}
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />
      {/* Q2 – analytes */}
      <QuestionItem
        questionIndex={"2"}
        questionName={"qEfficacyAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        isMultiSelectDropdown={true}
        globalId={globalId}
      />
      {/* Q3 – comparison names */}
      <QuestionsAnswersArray
        questionIndex={"3"}
        controlName={"qPrimaryEfficacyComparisonNames"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[
          ["Treatment", "Treatment", "select"],
          ["Comparator", "Comparator", "select"],
        ]}
        inputTextBoxesAdditionalData={"updateTgNamesComparisonAdditionalData"}
      />
      {/* Q4 – populations */}
      <QuestionsAnswersArray
        questionIndex={"4"}
        controlName={"qEfficacyPopulations"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q5 – analysis type names */}
      <QuestionsAnswersArray
        questionIndex={"5"}
        controlName={"qEfficacyAnalysisTypeNames"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q6 – baseline – hidden per manager request; logic/handlers kept intact
      <QuestionItem
        questionIndex={"6"}
        questionName={"qEfficacyBaseline"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        questionInputType={"text"}
      />
      */}
      {/* Q6 – timepoint names */}
      <QuestionsAnswersArray
        questionIndex={"6"}
        controlName={"qEfficacyTimepointNames"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* Q7 – primary efficacy? */}
      <QuestionItem
        questionIndex={"7"}
        questionName={"qPrimaryEfficacy"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        questionInputType={"radio"}
        questionInputChoice={"yes_no"}
        globalId={globalId}
      />
      {/* Q7a-Q7e – primary efficacy sub-questions */}
      <QuestionsAnswersArray
        questionIndex={"7a"}
        controlName={"qPrimaryEfficacyAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "true" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyAnalytes}
      />
      <QuestionsAnswersArray
        questionIndex={"7b"}
        controlName={"qPrimaryEfficacyPopulation"}
        activator={
          wizardForm.controls.qComparative.value === "true" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyPopulations}
      />
      <QuestionsAnswersArray
        questionIndex={"7c"}
        controlName={"qPrimaryEfficacyAnalysisTypeNames"}
        activator={
          wizardForm.controls.qComparative.value === "true" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={
          wizardForm.value.qEfficacyAnalysisTypeNames
        }
      />
      <QuestionsAnswersArray
        questionIndex={"7d"}
        controlName={"qPrimaryEfficacyAnalytesComparison"}
        activator={
          wizardForm.controls.qComparative.value === "true" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
      {/* <QuestionsAnswersArray
        questionIndex={"7e"}
        controlName={"qPrimaryEfficacyBaseline"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isPrimary
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyBaseline}
        maxInputs={1}
      /> */}
      <QuestionsAnswersArray
        questionIndex={"7e"}
        controlName={"qPrimaryEfficacyTimepointNames"}
        activator={
          wizardForm.controls.qComparative.value === "true" &&
          isPrimary &&
          isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "select_multiple"]]}
        inputTextBoxesAdditionalData={wizardForm.value.qEfficacyTimepointNames}
      />
      {/* Q8 – Kaplan-Meier plots (multiple-arm / dose-finding) */}
      <QuestionsAnswersArray
        questionIndex={"8"}
        controlName={"qEfficacyKaplanMeierAnalytes"}
        activator={
          wizardForm.controls.qComparative.value === "true" && isEfficacy
        }
        formGroup={wizardForm}
        inputTextBoxes={[["", "valueHelper", "text"]]}
      />
    </form>
  );
};

export default EfficacyAnalysisQuestions;
